import { Outlet } from 'react-router-dom';
import { useRouteMatch } from '../../shared/useRouteMatch';
import { Link } from 'react-router-dom';
import { pagesConfig, settingsDetailPages } from '../../shared/pages-config';
import { useUserSettings } from '../../utils/use-user-settings';
import { isAbleToSeeUIElement } from '../../utils/roles/client-roles';
import { isFeatureEnabled } from '../../utils/feature-flags';
import { Messages } from '../../utils/message-constants';
import SimpleBreadcrumbsPage from '../../shared/simple-breadcrumbs-page';
import { ModuleList } from '../../shared/analytics/module-list';
import SharedTabCard from '../../shared/shared-tab-card';
import SharedTabContentCard from '../../shared/shared-tab-content-card';
import Stack from '@mui/material/Stack';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

export default function Settings() {
  const { administrationRoles, usersTier, isDefault, features } =
    useUserSettings();

  const routeMatch = useRouteMatch([
    `${pagesConfig.settings.link}/${pagesConfig.setGpanelAdmin.link}`,
    `${pagesConfig.settings.link}/${pagesConfig.ipIntrusion.link}`,
    `${pagesConfig.settings.link}/${pagesConfig.secondaryDomains.link}`,
    `${pagesConfig.settings.link}/${pagesConfig.timezone.link}`,
    `${pagesConfig.settings.link}/${pagesConfig.customLabeling.link}`,
    `${pagesConfig.settings.link}/${pagesConfig.sessionTimeout.link}`,
    `${pagesConfig.settings.link}/${pagesConfig.googleApiStatus.link}`,
    `${pagesConfig.settings.link}/${pagesConfig.welcomeTemplates.link}`,
    `${pagesConfig.settings.link}/${pagesConfig.integrations.link}`,
  ]);
  const currentTab =
    routeMatch?.pattern?.path ?? `${pagesConfig.setGpanelAdmin.link}`;
  const isChildPage = useRouteMatch([
    `${pagesConfig.settings.link}/${pagesConfig.welcomeTemplates.link}/${pagesConfig.welcomeTemplatesFormPage.link}`,
    `${pagesConfig.settings.link}/${pagesConfig.welcomeTemplates.link}/${pagesConfig.editWelcomeTemplatesFormPage.link}`,
    `${pagesConfig.settings.link}/${pagesConfig.integrations.link}/${pagesConfig.manageSlackChannels.link}`,
  ]);

  if (isChildPage) return <Outlet />;

  return (
    <SimpleBreadcrumbsPage
      breadcrumbs={[
        { label: ModuleList.modules.administration.name },
        { label: pagesConfig.settings.name },
      ]}
      shouldAllowHeightToScroll
      bookmarkKey={pagesConfig.settings.value}
    >
      <Box>
        <Stack>
          <SharedTabCard>
            <Tabs
              value={currentTab.split('/').pop()}
              aria-label="Set gPanel Admin"
              variant="scrollable"
              allowScrollButtonsMobile
              scrollButtons={true}
              sx={{
                mx: 1,
                '& .MuiTabs-flexContainer': {
                  marginTop: '6px',
                },
                '.MuiTabs-scrollButtons.Mui-disabled': {
                  opacity: 0.3,
                },
              }}
            >
              {settingsDetailPages.map((page) => {
                if (
                  page.requiredFeatureFlag &&
                  !isFeatureEnabled(page.requiredFeatureFlag, features)
                ) {
                  return null;
                }
                const doesntHavePermission =
                  !isAbleToSeeUIElement(
                    administrationRoles,
                    page.requiredPermission
                  ) && page.requiredPermission !== '*';
                const doesntHaveTier = usersTier.rank < page.minimumTier;
                const shouldShowButDisabled = isDefault && doesntHaveTier;
                if (shouldShowButDisabled) {
                  return (
                    <Tab
                      key={page.name}
                      value={page.link}
                      component={Link}
                      to={page.link}
                      label={
                        <Tooltip
                          title={Messages.Errors.Tier.Viewing(
                            page.minimumTierDisplayValue
                          )}
                          onClick={(e) => e.preventDefault()}
                          arrow
                        >
                          <span style={{ pointerEvents: 'auto' }}>
                            {page.name}
                          </span>
                        </Tooltip>
                      }
                      icon={<page.icon />}
                      iconPosition="start"
                      sx={{
                        minHeight: '40px',
                        height: '40px',
                        width: 'fit-content',
                      }}
                      disabled
                    />
                  );
                }
                return (
                  !(doesntHavePermission || doesntHaveTier) && (
                    <Tab
                      key={page.name}
                      value={page.link}
                      component={Link}
                      to={page.link}
                      label={page.name}
                      icon={<page.icon />}
                      iconPosition="start"
                      sx={{
                        minHeight: '40px',
                        height: '40px',
                        width: 'fit-content',
                      }}
                    />
                  )
                );
              })}
            </Tabs>
          </SharedTabCard>
          <SharedTabContentCard
            sx={{
              minHeight: 420,
            }}
          >
            <Outlet />
          </SharedTabContentCard>
        </Stack>
      </Box>
    </SimpleBreadcrumbsPage>
  );
}
